import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import {
  DashboardAttentionEngagementClient,
  SupervisorTeamClient,
} from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  BooleanIndicator,
  PortalDataTable,
  PortalTableColumn,
  RelationshipPillList,
  StatusPill,
  SudsPill,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  ClientListFilters,
  ClientSignalFilter,
  initialClientSignalFilter,
} from "../components/ClientListFilters";
import { EngagementSignal } from "../components/EngagementSignal";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { TeamTabs } from "../components/PortalNavigation";
import { PortalLink } from "../components/PortalLink";
import { TeamClinicianFilter } from "../components/TeamClinicianFilter";
import {
  TeamClinicFilter,
  TeamClinicUnavailableState,
} from "../components/TeamClinicFilter";
import { useClinicianAssistantTeamScopeFilters } from "../components/ClinicianAssistantTrigger";
import { teamClientDetailPath } from "../config/navigation";
import { engagementStatusPresentation } from "../config/engagementStatus";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import {
  comparePortalNumber,
  comparePortalText,
  PortalSortDirection,
  stablePortalSort,
} from "../utils/tableSort";
import {
  uniqueSupervisorTeamClients,
  uniqueSupervisorTeamMembers,
  useTeamClinicScope,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

type TeamClientStatusFilter = "activated" | "all";
type TeamClientSortKey =
  | "signal"
  | "client"
  | "clinic"
  | "clinician"
  | "status"
  | "clinicalSummary"
  | "highSudsThreshold"
  | "selfInitiatedPermitted";

function initialFilterValue(
  key: "therapistId" | "search" | "clientStatus" | "sort" | "direction",
): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(key)?.trim() ?? "";
}

function teamClientColumns(
  clinicianCode: string,
  options: {
    clinicId: string;
    selectedTherapistId: string;
    showClinician: boolean;
    showStatus: boolean;
    engagementByClientId: ReadonlyMap<
      string,
      DashboardAttentionEngagementClient
    >;
    sortProps: (
      key: TeamClientSortKey,
    ) => Pick<
      PortalTableColumn<SupervisorTeamClient>,
      "sortDirection" | "onSort"
    >;
  },
): readonly PortalTableColumn<SupervisorTeamClient>[] {
  return [
    {
      key: "signal",
      header: "Signal",
      headerLabel: "Signal",
      initialWidth: 76,
      minWidth: 76,
      resizable: false,
      cellClassName: "cp-table-cell--signal",
      ...options.sortProps("signal"),
      render: (client) => (
        <EngagementSignal
          client={options.engagementByClientId.get(client.id)}
          unavailableLabel={
            client.status.trim().toLowerCase() === "activated"
              ? "Engagement signal unavailable"
              : "Not evaluated — client is not activated"
          }
        />
      ),
    },
    {
      key: "client",
      header: "Client",
      initialWidth: 190,
      minWidth: 160,
      ...options.sortProps("client"),
      render: (client) => {
        const clientName = client.nickname || client.accessCode;
        return (
          <PortalLink
            className="cp-team-client-link"
            path={withTeamClinicScope(
              teamClientDetailPath(client.id),
              options.clinicId,
            )}
            clinicianCode={clinicianCode}
            aria-label={
              client.nickname
                ? `${client.nickname} (${client.accessCode})`
                : client.accessCode
            }
          >
            <strong>{clientName}</strong>
            {client.nickname ? <small>{client.accessCode}</small> : null}
            {!options.showClinician && client.assignedTherapists.length > 1 ? (
              <small>
                Shared with{" "}
                {client.assignedTherapists
                  .filter(
                    (therapist) => therapist.id !== options.selectedTherapistId,
                  )
                  .map((therapist) => therapist.therapistName)
                  .join(", ")}
              </small>
            ) : null}
          </PortalLink>
        );
      },
    },
    {
      key: "clinic",
      header: "Clinic",
      initialWidth: 210,
      minWidth: 170,
      ...options.sortProps("clinic"),
      render: (client) =>
        client.clinicName ? (
          <RelationshipPillList names={[client.clinicName]} />
        ) : (
          <span className="cp-empty-value">Unassigned</span>
        ),
    },
    ...(options.showClinician
      ? [
          {
            key: "clinician",
            header: "Clinician",
            initialWidth: 210,
            minWidth: 160,
            ...options.sortProps("clinician"),
            render: (client: SupervisorTeamClient) => (
              <RelationshipPillList
                names={client.assignedTherapists.map(
                  (therapist) => therapist.therapistName,
                )}
              />
            ),
          },
        ]
      : []),
    ...(options.showStatus
      ? [
          {
            key: "status",
            header: "Status",
            initialWidth: 110,
            minWidth: 110,
            ...options.sortProps("status"),
            render: (client: SupervisorTeamClient) => (
              <StatusPill status={client.status} />
            ),
          },
        ]
      : []),
    {
      key: "clinicalSummary",
      header: "Clinical Summary",
      initialWidth: 360,
      minWidth: 240,
      ...options.sortProps("clinicalSummary"),
      render: (client) =>
        client.clinicalSummary ? (
          <span
            className="cp-team-client-summary"
            title={client.clinicalSummary}
          >
            {client.clinicalSummary}
          </span>
        ) : (
          <span className="cp-empty-value">Not provided</span>
        ),
    },
    {
      key: "highSudsThreshold",
      header: "High SUDS threshold",
      initialWidth: 150,
      minWidth: 150,
      ...options.sortProps("highSudsThreshold"),
      render: (client) => <SudsPill value={client.highSudsThreshold} />,
    },
    {
      key: "selfInitiatedPermitted",
      header: "Self-initiated",
      initialWidth: 120,
      minWidth: 120,
      resizable: false,
      ...options.sortProps("selfInitiatedPermitted"),
      render: (client) => (
        <BooleanIndicator value={client.selfInitiatedPermitted} />
      ),
    },
  ];
}

export function TeamClientsPage({ clinicianCode }: { clinicianCode: string }) {
  const { clinicId, setClinicId } = useTeamClinicScope();
  const [statusFilter, setStatusFilter] =
    useState<TeamClientStatusFilter>(() =>
      initialFilterValue("clientStatus") === "all" ? "all" : "activated",
    );
  const [search, setSearch] = useState(() => initialFilterValue("search"));
  const [signalFilter, setSignalFilter] = useState<ClientSignalFilter>(
    initialClientSignalFilter,
  );
  const [sortKey, setSortKey] = useState<TeamClientSortKey | null>(() => {
    const value = initialFilterValue("sort");
    return [
      "signal",
      "client",
      "clinic",
      "clinician",
      "status",
      "clinicalSummary",
      "highSudsThreshold",
      "selfInitiatedPermitted",
    ].includes(value)
      ? (value as TeamClientSortKey)
      : null;
  });
  const [sortDirection, setSortDirection] =
    useState<PortalSortDirection>(() =>
      initialFilterValue("direction") === "descending"
        ? "descending"
        : "ascending",
    );
  const [therapistId, setTherapistId] = useState(() =>
    initialFilterValue("therapistId"),
  );
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamClients(
        clinicianCode,
        clinicId,
        signal,
      ),
    [clinicId, clinicianCode],
  );
  const state = usePortalData(request);
  const engagementRequest = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamAttention(
        clinicianCode,
        clinicId,
        signal,
      ),
    [clinicId, clinicianCode],
  );
  const engagementState = usePortalData(engagementRequest);
  const engagementData =
    engagementState.status === "success" ||
    engagementState.status === "refreshing"
      ? engagementState.data
      : undefined;
  const engagementByClientId = useMemo(
    () =>
      new Map<string, DashboardAttentionEngagementClient>(
        (engagementData?.engagementClients ?? []).map((client) => [
          client.clientId,
          client,
        ]),
      ),
    [engagementData],
  );
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist?.role === "supervisor",
  );
  const clinicOptions = data?.clinicOptions ?? data?.clinics ?? [];
  const staleClinic = Boolean(
    data && clinicId && !clinicOptions.some((clinic) => clinic.id === clinicId),
  );
  const therapistOptions = useMemo(() => {
    if (!data) return [];
    return uniqueSupervisorTeamMembers(data.teamMembers).sort((left, right) =>
      left.therapistName.localeCompare(right.therapistName),
    );
  }, [data]);
  const effectiveTherapistId = therapistOptions.some(
    (member) => member.id === therapistId,
  )
    ? therapistId
    : "";
  useEffect(() => {
    const url = new URL(window.location.href);
    const setOrDelete = (key: string, value: string) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    };
    setOrDelete("therapistId", effectiveTherapistId);
    setOrDelete("search", search.trim());
    setOrDelete("signal", signalFilter);
    setOrDelete("clientStatus", statusFilter === "all" ? "all" : "");
    setOrDelete("sort", sortKey ?? "");
    setOrDelete("direction", sortKey ? sortDirection : "");
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [effectiveTherapistId, search, signalFilter, sortDirection, sortKey, statusFilter]);
  useClinicianAssistantTeamScopeFilters({
    clinicId,
    therapistId: effectiveTherapistId,
  });
  const statusScopedClients = useMemo(() => {
    if (!data) return [];
    return uniqueSupervisorTeamClients(data.clients).filter((client) => {
      if (
        statusFilter === "activated" &&
        client.status.trim().toLowerCase() !== "activated"
      ) {
        return false;
      }
      return true;
    });
  }, [data, statusFilter]);
  const filteredClients = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return statusScopedClients.filter((client) => {
      if (
        signalFilter &&
        engagementByClientId.get(client.id)?.engagementStatus !== signalFilter
      ) {
        return false;
      }
      if (!query) return true;
      return [
        client.accessCode,
        client.nickname,
        client.clinicalSummary,
        client.clinicName,
        ...client.assignedTherapists.map(
          (therapist) => therapist.therapistName,
        ),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [engagementByClientId, search, signalFilter, statusScopedClients]);
  const clinicianClientCounts = useMemo(() => {
    const counts = new Map<string, number>();
    therapistOptions.forEach((member) => counts.set(member.id, 0));
    statusScopedClients.forEach((client) => {
      client.assignedTherapists.forEach((therapist) => {
        if (counts.has(therapist.id)) {
          counts.set(therapist.id, (counts.get(therapist.id) ?? 0) + 1);
        }
      });
    });
    return counts;
  }, [statusScopedClients, therapistOptions]);
  const visibleClients = useMemo(
    () =>
      effectiveTherapistId
        ? filteredClients.filter((client) =>
            client.assignedTherapists.some(
              (therapist) => therapist.id === effectiveTherapistId,
            ),
          )
        : filteredClients,
    [effectiveTherapistId, filteredClients],
  );
  const sortedClients = useMemo(() => {
    if (!sortKey) return visibleClients;
    return stablePortalSort(visibleClients, (left, right) => {
      switch (sortKey) {
        case "signal": {
          const leftStatus = engagementByClientId.get(
            left.id,
          )?.engagementStatus;
          const rightStatus = engagementByClientId.get(
            right.id,
          )?.engagementStatus;
          return comparePortalText(
            leftStatus ? engagementStatusPresentation[leftStatus].label : "",
            rightStatus ? engagementStatusPresentation[rightStatus].label : "",
            sortDirection,
          );
        }
        case "client":
          return (
            comparePortalText(
              left.nickname || left.accessCode,
              right.nickname || right.accessCode,
              sortDirection,
            ) ||
            comparePortalText(left.accessCode, right.accessCode, sortDirection)
          );
        case "clinic":
          return comparePortalText(
            left.clinicName,
            right.clinicName,
            sortDirection,
          );
        case "clinician":
          return comparePortalText(
            left.assignedTherapists
              .map((therapist) => therapist.therapistName)
              .sort()
              .join(" "),
            right.assignedTherapists
              .map((therapist) => therapist.therapistName)
              .sort()
              .join(" "),
            sortDirection,
          );
        case "highSudsThreshold":
          return comparePortalNumber(
            left.highSudsThreshold,
            right.highSudsThreshold,
            sortDirection,
          );
        case "selfInitiatedPermitted":
          return comparePortalNumber(
            Number(left.selfInitiatedPermitted),
            Number(right.selfInitiatedPermitted),
            sortDirection,
          );
        default:
          return comparePortalText(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
      }
    });
  }, [engagementByClientId, sortDirection, sortKey, visibleClients]);
  const updateSort = (nextSortKey: TeamClientSortKey): void => {
    setSortDirection(
      nextSortKey === sortKey && sortDirection === "ascending"
        ? "descending"
        : "ascending",
    );
    setSortKey(nextSortKey);
  };
  const sortProps = (key: TeamClientSortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });
  const columns = teamClientColumns(clinicianCode, {
    clinicId,
    selectedTherapistId: effectiveTherapistId,
    showClinician: !effectiveTherapistId,
    showStatus: statusFilter === "all",
    engagementByClientId,
    sortProps,
  });
  const refreshing =
    state.status === "refreshing" || engagementState.status === "refreshing";
  const refresh = () => {
    state.refresh();
    engagementState.refresh();
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="team"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Team Clients"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        clinicianNameLoading={state.status === "loading"}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Home", path: "/clinician-portal" },
              {
                label: "Team",
                path: withTeamClinicScope("/clinician-portal/team", clinicId),
              },
              { label: "Clients" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        <TeamTabs
          clinicianCode={clinicianCode}
          activeKey="clients"
          clinicId={clinicId}
        />
        <div
          className="cp-filter-group cp-team-client-status-tabs"
          aria-label="Team client status filter"
        >
          <button
            type="button"
            aria-pressed={statusFilter === "activated"}
            onClick={() => setStatusFilter("activated")}
          >
            Activated Clients
          </button>
          <button
            type="button"
            aria-pressed={statusFilter === "all"}
            onClick={() => setStatusFilter("all")}
          >
            All Clients
          </button>
        </div>
        {state.status === "loading" ? (
          <LoadingState label="Loading team clients" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Team clients are unavailable")} />
        ) : null}
        {available && data && staleClinic ? (
          <TeamClinicUnavailableState
            onViewAllClinics={() => setClinicId("")}
          />
        ) : null}
        {available && data && !staleClinic ? (
          <section aria-label="Team clients">
            <div className="cp-content-header">
              <div>
                <p>
                  Review clients within your authorized clinics. This view is
                  read-only.
                </p>
              </div>
              <strong className="cp-record-count">
                {visibleClients.length}{" "}
                {visibleClients.length === 1 ? "client" : "clients"}
              </strong>
            </div>
            <div
              className="cp-team-client-filters"
              aria-label="Team client filters"
            >
              <TeamClinicianFilter
                value={effectiveTherapistId}
                allCount={statusScopedClients.length}
                options={therapistOptions.map((member) => ({
                  id: member.id,
                  name: member.therapistName,
                  isSupervisor: member.memberships.some(
                    (membership) => membership.membershipRole === "supervisor",
                  ),
                  isCurrentUser: member.isCurrentUser,
                  count: clinicianClientCounts.get(member.id) ?? 0,
                }))}
                onChange={setTherapistId}
                countLabel="client"
                countLabelPlural="clients"
                groupLabel="Clinician caseload"
              />
              <button
                type="button"
                className="cp-button cp-button--secondary"
                onClick={refresh}
                disabled={refreshing}
              >
                <RefreshCw aria-hidden="true" />
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
            <div className="cp-list-toolbar cp-list-toolbar--clients cp-team-client-secondary-filters">
              <ClientListFilters
                idPrefix="team-clients"
                search={search}
                signal={signalFilter}
                onSearchChange={setSearch}
                onSignalChange={setSignalFilter}
                afterSearch={
                  <TeamClinicFilter
                    clinicOptions={clinicOptions}
                    value={clinicId}
                    compact
                    onChange={(nextClinicId) => {
                      setClinicId(nextClinicId);
                      setTherapistId("");
                    }}
                  />
                }
              />
            </div>
            {engagementState.status === "error" ? (
              <div className="cp-attention-error" role="alert">
                <AlertTriangle aria-hidden="true" />
                <span>Signal data is temporarily unavailable.</span>
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  onClick={engagementState.refresh}
                >
                  Try again
                </button>
              </div>
            ) : null}
            {sortedClients.length ? (
              <PortalDataTable
                caption="Team Clients"
                columns={columns}
                records={sortedClients}
                getRecordKey={(client) => client.id}
                getRecordOpenLabel={(client) =>
                  `View ${client.nickname || client.accessCode}'s team client record`
                }
                onRecordOpen={(client) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      withTeamClinicScope(
                        teamClientDetailPath(client.id),
                        clinicId,
                      ),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <EmptyState
                title={
                  clinicId
                    ? "No clients were found for the selected clinic."
                    : "No team clients found"
                }
                description="No clients match the selected status, clinician, search, and Signal filters."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
